'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Line, LineChart, XAxis, YAxis } from 'recharts';

import { ChartContainer as ShadcnChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { formatMoney } from "@/lib/money";
import { EmptyState } from './EmptyState';
import { formatBucketDate } from '../utils/formatBucketDate';

export type AnalyticsDataPoint = { code: string; value: number; volume?: number; currency?: string };

interface ChartContainerProps {
    data: AnalyticsDataPoint[];
    chartType?: 'bar' | 'line';
    currency?: string;
    isPercentage?: boolean;
    className?: string;
    emptyStateMessage?: string;
}

export function ChartContainer({ data, chartType = 'bar', currency, isPercentage = false, className = '', emptyStateMessage }: ChartContainerProps) {
    const { t } = useTranslation('analytics');
    const isTimeSeries = data && data.length > 0 && typeof data[0].code === 'string' && /^\d{4}-\d{2}-\d{2}/.test(String(data[0].code));
    const sortedData = isTimeSeries ? [...data].sort((a, b) => new Date(a.code).getTime() - new Date(b.code).getTime()) : [...data].sort((a, b) => b.value - a.value);

    const config = { value: { label: 'Value', color: 'var(--chart-1)' } } as const;

    const formatYAxisValue = (value: number) => {
        if (isPercentage) return `${Math.round(value)}%`;
        if (currency) {
            const majorUnits = value / 100;
            if (majorUnits >= 1000000) return `${(majorUnits / 1000000).toFixed(1)}M`;
            if (majorUnits >= 1000) return `${(majorUnits / 1000).toFixed(1)}K`;
            try { return formatMoney(value, currency).replace(/\.00$/, ''); } catch { return `${value}`; }
        }
        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
        return Math.round(value).toString();
    };

    const getTooltipText = (value: number, volumeValue?: number) => {
        if (typeof value !== 'number') return 'N/A';
        if (isPercentage) return `${Math.round(value)}%`;
        if (currency) {
            const mainValue = formatMoney(value, currency);
            if (volumeValue && volumeValue > 0) {
                const countValue = volumeValue.toLocaleString();
                return `${mainValue} (${countValue} ${t('charts.transactions')})`;
            }
            return mainValue;
        }
        return value.toLocaleString();
    };


    if (!data || data.length === 0) {
        return <EmptyState message={emptyStateMessage || t('charts.noData')} className={className} />;
    }

    if (chartType === 'bar') {
        return (
            <ShadcnChartContainer config={config} className={`w-full h-64 ${className}`}>
                <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <XAxis dataKey="code" tickLine={false} axisLine={false} tickFormatter={isTimeSeries ? (v) => formatBucketDate(String(v)) : undefined} />
                    <YAxis tickFormatter={formatYAxisValue} domain={isPercentage ? [0, 100] : ['auto', 'auto']} />
                    <ChartTooltip content={<ChartTooltipContent labelFormatter={(label) => isTimeSeries ? formatBucketDate(String(label)) : String(label)} formatter={(value, name, item) => getTooltipText(Number(value), typeof (item?.payload as any)?.volume === 'number' ? Number((item?.payload as any)?.volume) : undefined)} />} />
                    <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} />
                </BarChart>
            </ShadcnChartContainer>
        );
    }

    return (
        <ShadcnChartContainer config={config} className={`w-full h-64 ${className}`}>
            <LineChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                <XAxis dataKey="code" tickLine={false} axisLine={false} tickFormatter={isTimeSeries ? (v) => formatBucketDate(String(v)) : undefined} />
                <YAxis tickFormatter={formatYAxisValue} domain={isPercentage ? [0, 100] : ['auto', 'auto']} />
                <ChartTooltip content={<ChartTooltipContent labelFormatter={(label) => isTimeSeries ? formatBucketDate(String(label)) : String(label)} formatter={(value, name, item) => getTooltipText(Number(value), typeof (item?.payload as any)?.volume === 'number' ? Number((item?.payload as any)?.volume) : undefined)} />} />
                <Line type="monotone" dataKey="value" stroke="var(--color-value)" strokeWidth={2} dot={false} activeDot={{ r: 6, strokeWidth: 2 }} />
            </LineChart>
        </ShadcnChartContainer>
    );
}


